import React from "react";
import {
  MdChevronLeft,
  MdChevronRight,
  MdKeyboardArrowDown,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import styles from "./InternalPagination.module.css";

const InternalPagination = ({
  page = 1,
  limit = 10,
  total = 0,
  totalPages,
  limitOptions = [],
  onPageChange,
  onLimitChange,
  rowsLabel = "Rows per page:",
  showFirstLast = true,
  className = "",
}) => {
  const resolvedTotalPages = Math.max(1, Number(totalPages) || Math.ceil((Number(total) || 0) / (Number(limit) || 1)) || 1);
  const currentPage = Math.min(Math.max(1, Number(page) || 1), resolvedTotalPages);
  const showLimit = Array.isArray(limitOptions) && limitOptions.length > 0 && onLimitChange;

  const handlePageChange = (nextPage) => {
    if (!onPageChange) return;
    const clampedPage = Math.min(Math.max(1, nextPage), resolvedTotalPages);
    if (clampedPage !== currentPage) {
      onPageChange(clampedPage);
    }
  };

  // Calculate range display
  const start = total > 0 ? (currentPage - 1) * limit + 1 : 0;
  const end = total > 0 ? Math.min(currentPage * limit, total) : 0;

  return (
    <div className={`${styles.pagination} ${className}`.trim()}>
      {total > 0 && (
        <div className={styles.resultsInfo}>
          <span className="hidden sm:inline">Showing </span>
          <strong className={styles.highlight}>{start}</strong>–<strong className={styles.highlight}>{end}</strong>
          <span> of </span>
          <strong className={styles.highlight}>{total}</strong>
          <span className="hidden sm:inline"> items</span>
        </div>
      )}

      <div className={styles.controlsGroup}>
        {showLimit && (
          <label className={styles.limitControl}>
            <span className={styles.limitLabel}>
              <span className="hidden sm:inline">{rowsLabel}</span>
              <span className="sm:hidden">Rows:</span>
            </span>
            <span className={styles.selectWrap}>
              <select
                value={limit}
                onChange={(event) => onLimitChange(Number(event.target.value) || limit)}
              >
                {limitOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <MdKeyboardArrowDown className={styles.selectIcon} size={16} />
            </span>
          </label>
        )}

        <span className={styles.pageInfo}>
          <span className="hidden sm:inline">Page </span>
          <span>{currentPage}</span>
          <span className="hidden sm:inline"> of </span>
          <span className="sm:hidden">/</span>
          <span>{resolvedTotalPages}</span>
        </span>

        <div className={styles.navButtonGroup}>
          {showFirstLast && (
            <button
              type="button"
              className={`${styles.navButton} ${styles.firstLastBtn}`}
              onClick={() => handlePageChange(1)}
              disabled={currentPage <= 1}
              aria-label="First page"
              title="First page"
            >
              <MdKeyboardDoubleArrowLeft size={16} />
            </button>
          )}

          <button
            type="button"
            className={styles.navButton}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            title="Previous page"
          >
            <MdChevronLeft size={16} />
          </button>

          <button
            type="button"
            className={styles.navButton}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= resolvedTotalPages}
            aria-label="Next page"
            title="Next page"
          >
            <MdChevronRight size={16} />
          </button>

          {showFirstLast && (
            <button
              type="button"
              className={`${styles.navButton} ${styles.firstLastBtn}`}
              onClick={() => handlePageChange(resolvedTotalPages)}
              disabled={currentPage >= resolvedTotalPages}
              aria-label="Last page"
              title="Last page"
            >
              <MdKeyboardDoubleArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InternalPagination;
